import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';

export interface ConfirmationModalData {
  title: string;
  description: string;
  cancelText?: string;
  confirmText?: string;
  imageSrc?: string;
  imageAlt?: string;
  showTimeInfo?: boolean;
  timeInfo?: string;
  showCancelButton?: boolean;
  showConfirmButton?: boolean;
  isSuccessModal?: boolean;
  isLoading?: boolean;
}

/**
 * Confirmation modal dialog component for prompting users to confirm or cancel actions with configurable text, imagery, and optional loading/success states.
 * @component
 * @selector app-confirmation-modal
 * @example
 *   <app-confirmation-modal
 *     [isVisible]="true"
 *     [data]="{ title: 'Delete Item', description: 'Are you sure?', confirmText: 'Delete', cancelText: 'Cancel' }"
 *     (cancel)="onCancelHandler()"
 *     (confirm)="onConfirmHandler()"
 *   ></app-confirmation-modal>
 * @input boolean isVisible - Controls whether the modal is visible. When true the modal is shown; when false it is hidden.
 * @input ConfirmationModalData data - Configuration object for modal content and behavior. Fields include title, description, cancelText, confirmText, imageSrc, imageAlt, showTimeInfo, timeInfo, showCancelButton, showConfirmButton, isSuccessModal, and isLoading. Defaults are provided for all fields.
 * @output EventEmitter<void> cancel - Emitted when the user cancels the modal (via the cancel button or overlay click) if the modal is not in a loading state.
 * @output EventEmitter<void> confirm - Emitted when the user confirms the action via the confirm button if the modal is not in a loading state.
 * Key methods:
 *   - get showCancelButton(): boolean — Returns whether the cancel button should be shown, defaulting to true when unspecified.
 *   - get showConfirmButton(): boolean — Returns whether the confirm button should be shown, defaulting to true when unspecified.
 *   - onCancel(): void — Triggers the cancel event (no-op when isLoading is true).
 *   - onConfirm(): void — Triggers the confirm event (no-op when isLoading is true).
 *   - onOverlayClick(event: Event): void — Detects overlay clicks and triggers cancel when clicking the backdrop (ignores when isLoading is true).
 * Lifecycle:
 *   - No Angular lifecycle hooks are implemented on this component; behavior is driven by @Input properties and user interactions.
 */
@Component({
  selector: 'app-confirmation-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './confirmation-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./confirmation-modal.component.scss'],
})
export class ConfirmationModalComponent {
  @Input() isVisible = false;
  @Input() data: ConfirmationModalData = {
    title: 'Confirm Action',
    description: 'Are you sure you want to proceed?',
    cancelText: 'Cancel',
    confirmText: 'Confirm',
    imageSrc: 'assets/empty_screen.png',
    imageAlt: 'Confirmation illustration',
    showTimeInfo: false,
    timeInfo: '',
    showCancelButton: true,
    showConfirmButton: true,
    isSuccessModal: false,
    isLoading: false,
  };

  // Getter methods to safely access button visibility with defaults
  get showCancelButton(): boolean {
    return this.data.showCancelButton !== false; // Default to true if not specified
  }

  get showConfirmButton(): boolean {
    return this.data.showConfirmButton !== false; // Default to true if not specified
  }

  @Output() cancel = new EventEmitter<void>();
  @Output() confirm = new EventEmitter<void>();

  onCancel(): void {
    if (!this.data.isLoading) {
      this.cancel.emit();
    }
  }

  onConfirm(): void {
    if (!this.data.isLoading) {
      this.confirm.emit();
    }
  }

  onOverlayClick(event: Event): void {
    if (event.target === event.currentTarget && !this.data.isLoading) {
      this.onCancel();
    }
  }
}
